import { createContext, useContext } from "react";
import { runInAction, makeAutoObservable } from "mobx";
import {
  Category,
  Coordinate,
  Event,
  Organization,
} from "@root/utils/entities";
import {
  DateRange,
  FilterType,
  MapBounds,
  NumberRange,
} from "@root/utils/types";
import { eventService } from "@root/services/api/eventService";
import { userService } from "@root/services/api/userService";
import {
  getApiError,
  hasErrors,
  parseEventFromBackend,
} from "@root/utils/functions";
import { ModalStore } from "./modalStore";
import { EventSearchDefaults } from "@root/utils/enums";
import { PaginationStore } from "./paginationStore";

type ViewMode = "list" | "map";
export type EventSearchFilters = {
  address?: string;
  mileRadius: number;
  ageRange: NumberRange;
  dateRange?: DateRange;
  categories?: string[];
  coordinate?: Coordinate;
};

export class EventPageStore {
  private readonly _defaultFilters: EventSearchFilters = {
    address: "",
    mileRadius: EventSearchDefaults.MileRadius,
    ageRange: {
      min: EventSearchDefaults.MinAge,
      max: EventSearchDefaults.MaxAge,
    },
  };

  readonly paginationStore;
  readonly viewModalStore;
  readonly orgModalStore;

  isLoading: boolean = false;
  isSearching: boolean = false;
  fitMap: boolean = false;
  zoomLevel?: number;
  error?: string;
  categories: Category[];
  events?: Event[];
  filters: EventSearchFilters;
  mapBounds?: MapBounds;
  viewMode: ViewMode = "map";
  event?: Event;
  organization?: Organization;
  currentSubview?: "event" | "organization";
  userId?: string;

  constructor() {
    makeAutoObservable(this);
    this.filters = this._defaultFilters;
    this.categories = [];
    this.events = [];
    this.paginationStore = new PaginationStore();
    this.viewModalStore = new ModalStore();
    this.orgModalStore = new ModalStore();
  }

  showLoading() {
    this.isLoading = true;
  }

  hideLoading() {
    this.isLoading = false;
  }

  showError(error?: string) {
    this.error = error;
    this.isLoading = false;
  }

  setUserId(userId?: string) {
    this.userId = userId;
  }

  searchEvents(
    userLocation?: Coordinate,
    fitMap: boolean = false,
    zoomLevel?: number,
    userId?: string,
  ) {
    const effectiveUserId = userId || this.userId;
    this.isSearching = true;

    eventService
      .searchEvents(
        this.paginationStore.page,
        this.filters.address,
        this.filters.mileRadius,
        this.filters.ageRange,
        this.filters.dateRange,
        this.filters.categories,
        this.mapBounds,
        userLocation,
      )
      .then(async (response) => {
        runInAction(() => {
          if (hasErrors(response)) {
            this.error = getApiError(response)?.message;
          } else {
            this.error = undefined;

            const rawEvents = response.data?.events ?? [];
            // Parse events from backend format
            const events = rawEvents.map((e: any) => parseEventFromBackend(e));
            const searchFilters = response.data?.searchFilters;
            const pagination = response.data?.pagination;

            // If user is logged in, fetch followed events and merge
            if (effectiveUserId) {
              userService
                .getFollowedEvents(effectiveUserId)
                .then((followedResponse) => {
                  runInAction(() => {
                    if (!hasErrors(followedResponse)) {
                      const followedEventIds = new Set(
                        followedResponse.data?.events?.map((e) => e.id) ?? [],
                      );

                      // Mark events as followed based on followed events list
                      events.forEach((event) => {
                        event.isFollowed = followedEventIds.has(event.id);
                      });
                    }

                    this.events = events;
                  });
                });
            } else {
              this.events = events;
            }

            this.filters.coordinate = searchFilters?.coordinate;

            this.fitMap = fitMap;
            this.zoomLevel = zoomLevel;
            this.paginationStore.setPagination(
              pagination?.page ?? 0,
              pagination?.pageCount ?? 0,
              pagination?.totalCount ?? 0,
            );
          }
          this.isSearching = false;
        });
      });
  }

  loadCategories() {
    eventService.getCategories().then((response) =>
      runInAction(() => {
        if (hasErrors(response)) {
          this.error = getApiError(response)?.message;
          this.categories = [];
        } else {
          this.categories = response.data?.categories ?? [];
        }
      }),
    );
  }

  setFilters(
    filterType: FilterType,
    filters: EventSearchFilters,
    userLocation?: Coordinate,
    search: boolean = false,
    fitMap: boolean = false,
  ) {
    this.filters = filters;

    if (filterType === "location") {
      this.mapBounds = undefined;
    }

    if (search) this.searchEvents(userLocation, fitMap);
  }

  setMapBounds(northEast: Coordinate, southWest: Coordinate) {
    if (
      this.mapBounds?.northEast.latitude === northEast.latitude &&
      this.mapBounds?.northEast.longitude === northEast.longitude &&
      this.mapBounds?.southWest.latitude === southWest.latitude &&
      this.mapBounds?.southWest.longitude === southWest.longitude
    )
      return;

    this.mapBounds = { northEast, southWest };
    this.fitMap = false;

    this.searchEvents();
  }

  showList() {
    this.viewMode = "list";
  }

  showMap() {
    this.viewMode = "map";
  }

  toggleViewMode() {
    if (this.viewMode === "map") this.showList();
    else this.showMap();
  }

  toggleFitMap(val: boolean) {
    this.fitMap = val;
    if (this.fitMap) this.mapBounds = undefined;
  }

  showEvent(event: Event) {
    this.event = event;
    this.currentSubview = this.organization ? "organization" : "event";
    this.viewModalStore.open();
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.set("event", event.id);
      window.history.replaceState(null, "", url.toString());
    }
    const eventId = event.id;
    eventService.getEvent(eventId).then((response) => {
      runInAction(() => {
        if (
          !hasErrors(response) &&
          response.data?.event &&
          this.event?.id === eventId
        ) {
          this.event = parseEventFromBackend(response.data.event);
        }
      });
    });
  }

  hideEvent() {
    this.viewModalStore.close();
    this.event = undefined;
    if (this.organization) {
      this.currentSubview = "organization";
      this.orgModalStore?.open();
    } else {
      this.currentSubview = undefined;
    }
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.delete("event");
      window.history.replaceState(null, "", url.toString());
    }
  }

  showOrganization(organization?: Organization) {
    this.organization = organization;
    this.currentSubview = this.event ? "event" : "organization";
    this.orgModalStore?.open();
  }

  hideOrganization() {
    this.orgModalStore?.close();
    this.organization = undefined;
    this.currentSubview = this.event ? "event" : undefined;
  }

  removeEvent(eventId: string) {
    if (this.events) {
      this.events = this.events.filter((event) => event.id !== eventId);
    }
  }

  // Sync follow state back into the list when it is changed from the detail view.
  setEventFollowState(eventId: string, isFollowed: boolean) {
    if (this.events) {
      const target = this.events.find((event) => event.id === eventId);
      if (target) {
        target.isFollowed = isFollowed;
        // Reassign to a new array reference so observers re-render the list.
        this.events = [...this.events];
      }
    }
    if (this.event?.id === eventId) {
      this.event.isFollowed = isFollowed;
    }
  }
}

export const EventPageContext = createContext<EventPageStore>(null!);
export const useEventPageContext = () => useContext(EventPageContext);
